import { useEffect, useRef, useState, useCallback } from 'react';
import { Message } from '../types';

type SectionCompletionStatus = 'LEARNED' | 'SKIPPED' | 'COMPLETED';

export const useWebSocket = (lessonId: string, chatId: string) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'disconnected'>('disconnected');
  const [completedSections, setCompletedSections] = useState<Record<string, SectionCompletionStatus>>({});
  const [currentSectionName, setCurrentSectionName] = useState<string>('');
  const [liveLessonCompletionCount, setLiveLessonCompletionCount] = useState(0);
  const ws = useRef<WebSocket | null>(null);
  const currentChatId = useRef<string>('');
  const isConnected = useRef<boolean>(false);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const reconnectAttemptRef = useRef(0);
  const shouldReconnectRef = useRef(false);

  const normalizeCompletionData = useCallback((completionData?: Message['completionData']) => {
    if (!completionData) return undefined;
    const raw = completionData as any;
    return {
      status: raw.status,
      sectionName: raw.sectionName || raw.section_name,
      lessonTitle: raw.lessonTitle || raw.lesson_title
    };
  }, []);

  const updateCompletedSections = useCallback((data: Message) => {
    if (data.type !== 'section_completion') return;
    const completion = normalizeCompletionData(data.completionData);
    const sectionName = completion?.sectionName || data.content;
    if (!sectionName) return;
    const status = completion?.status || 'COMPLETED';
    setCompletedSections(prev => ({ ...prev, [sectionName]: status }));
    setCurrentSectionName(prev => (prev === sectionName ? '' : prev));
  }, [normalizeCompletionData]);

  const updateCurrentSection = useCallback((data: Message) => {
    if (data.type !== 'section_switch') return;
    const sectionName = data.content || '';
    if (sectionName) {
      setCurrentSectionName(sectionName);
    }
  }, []);

  const sendMessage = useCallback((message: Message) => {
    if (ws.current && ws.current.readyState === WebSocket.OPEN && isConnected.current) {
      ws.current.send(JSON.stringify(message));
      console.log('Sent message:', message.type);
      
      // Add user message to chat if it's a user message
      if (message.type === 'user_message' && message.content) {
        const userMessage: Message = {
          type: 'user_message',
          content: message.content,
          timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, userMessage]);
      }

      if (message.type === 'section_switch' && message.content) {
        setCurrentSectionName(message.content);
      }
    }
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
    setCompletedSections({});
    setCurrentSectionName('');
    setLiveLessonCompletionCount(0);
  }, []);

  useEffect(() => {
    // Don't create a new connection if we already have one for this chat
    if (!chatId || (currentChatId.current === chatId && ws.current?.readyState === WebSocket.OPEN)) {
      return;
    }

    console.log(`In useEffect with chatId = ${chatId}`)

    // Close existing connection if switching chats
    if (ws.current) {
      shouldReconnectRef.current = false;
      ws.current.close();
      isConnected.current = false;
    }
    if (reconnectTimeoutRef.current !== null) {
      window.clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    shouldReconnectRef.current = true;
    reconnectAttemptRef.current = 0;
    currentChatId.current = chatId;
    setMessages([]);
    setCompletedSections({});
    setCurrentSectionName('');
    setLiveLessonCompletionCount(0);

    const connect = () => {
      if (!shouldReconnectRef.current) return;
      const wsBaseUrl = process.env.REACT_APP_WS_BASE_URL || 'ws://localhost:8500';
      const websocket = new WebSocket(`${wsBaseUrl}/ws/${lessonId}/${chatId}`);
      ws.current = websocket;
      setConnectionStatus('connecting');
      
      websocket.onopen = () => {
        if (websocket !== ws.current) return;
        isConnected.current = true;
        reconnectAttemptRef.current = 0;
        setConnectionStatus('connected');
        console.log('WebSocket connected for chat:', chatId);
      };

      websocket.onmessage = (event) => {
        if (websocket !== ws.current) return;
        try {
          const data: Message = JSON.parse(event.data);

          // Handle chat history message
          if (data.type === 'chat_history' && data.messages) {
            console.log('Received chat history with', data.messages.length, 'messages');
            console.log('chat history', data.messages);
            const historyMessages: Message[] = data.messages as Message[];
            const historyCompletions: Record<string, SectionCompletionStatus> = {};
            let historyCurrentSection = '';
            historyMessages.forEach((m: Message) => {
              if (m.type === 'section_completion') {
                const completion = normalizeCompletionData(m.completionData);
                const sectionName = completion?.sectionName || m.content;
                if (!sectionName) return;
                historyCompletions[sectionName] = completion?.status || 'COMPLETED';
                if (historyCurrentSection === sectionName) {
                  historyCurrentSection = '';
                }
              }
              if (m.type === 'section_switch') {
                const sectionName = m.content || '';
                if (sectionName) {
                  historyCurrentSection = sectionName;
                }
              }
            });
            setMessages(historyMessages);
            setCompletedSections(historyCompletions);
            setCurrentSectionName(historyCurrentSection);
            return;
          }

          // Handle regular individual messages
          updateCurrentSection(data);
          updateCompletedSections(data);
          if (data.type === 'lesson_completion') {
            setLiveLessonCompletionCount(count => count + 1);
          }
          setMessages(prev => [...prev, data]);
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error);
        }
      };

      websocket.onclose = () => {
        if (websocket !== ws.current) return;
        isConnected.current = false;
        setConnectionStatus('disconnected');
        console.log('WebSocket disconnected');
        if (!shouldReconnectRef.current || currentChatId.current !== chatId) return;
        const delay = Math.min(1000 * 2 ** reconnectAttemptRef.current, 10000);
        reconnectAttemptRef.current += 1;
        reconnectTimeoutRef.current = window.setTimeout(() => {
          reconnectTimeoutRef.current = null;
          connect();
        }, delay);
      };

      websocket.onerror = (error) => {
        if (websocket !== ws.current) return;
        console.error('WebSocket error:', error);
        isConnected.current = false;
        setConnectionStatus('disconnected');
        if (websocket.readyState !== WebSocket.CLOSED && websocket.readyState !== WebSocket.CLOSING) {
          websocket.close();
        }
      };
    };

    if (chatId) {
      connect();
    }

    return () => {
      shouldReconnectRef.current = false;
      if (reconnectTimeoutRef.current !== null) {
        window.clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (ws.current) {
        console.log("Closing connection due to effect destructor")
        isConnected.current = false;
        ws.current.close();
        ws.current = null;
      }
    };
  }, [chatId, lessonId, normalizeCompletionData, updateCompletedSections, updateCurrentSection]);

  return {
    messages,
    connectionStatus,
    sendMessage,
    clearMessages,
    completedSections,
    currentSectionName,
    liveLessonCompletionCount
  };
};
